// src/pages/ForumCategory.tsx
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate, useParams } from 'react-router-dom';
import { createReply } from '@/lib/supabase/forum';
import type { Member } from '@/types/user';
import { toggleTopicLike, getTopicLikeCount, getTopicLikeStatus } from '@/lib/supabase/forum';
import { ThumbsUp } from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import {
  ArrowLeft,
  Plus,
  MessageSquare,
  Loader2,
  Users,
  Calendar,
  Shield,
  Dumbbell,
  Trophy,
  MapPin,
  Briefcase,
  Coffee,
  Leaf,
  Pin,
  Lock,
  Eye,
  Clock,
  Trash
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import {
  getForumCategoryBySlug,
  getCategoryTopics,
  getTopic,
  getTopicReplies
} from '@/lib/supabase/forum';
import { ForumCategory as CategoryType, ForumTopic } from '@/types/forum';
import TopicHeader from '@/components/forums/TopicHeader';
import TopicContent from '@/components/forums/TopicContent';
import ReplyList from '@/components/forums/ReplyList';
import ReplyForm from '@/components/forums/ReplyForm';
import TopicList from '@/components/forums/TopicList';
import TopicListSkeleton from '@/components/forums/TopicListSkeleton';
import { useAuth } from '@/contexts/AuthContext';
import { formatRelativeTime } from '@/lib/utils/time';
import { supabase } from '@/lib/supabase';
import { updateTopic, deleteTopic } from '@/lib/supabase/forum';
import { getUserClubBadges, ClubBadge } from '@/lib/supabase/clubs-utils';
import ClubBadges from '@/components/shared/ClubBadges';

// Map category slugs to background images
const CATEGORY_BACKGROUNDS = {
  // Sports/Arena - Tennis court
  'sports-discussion': 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=1600&q=80',
  
  // Fitness/Sanctuary - Yoga studio
  'fitness-wellness': 'https://images.unsplash.com/photo-1694861786917-b53dd8a86ea6?auto=format&fit=crop&w=1600&q=80',
  
  // Clubhouse - Luxury club interior
    'club-recommendations': 'https://images.unsplash.com/photo-1603722346374-d5e3c673ad3f?auto=format&fit=crop&w=1600&q=80',
  
  // Deal Room - Business meeting
  'business-networking': 'https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=80',
  
  // Atlas - Travel map
  'travel-locations': 'https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=1600&q=80',
  
  // Lounge - Cozy coffee shop
  'general-discussion': 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1600&q=80',
  
  // Rendezvous - Social gathering
  'the-rendezvous': 'https://images.unsplash.com/photo-1495837174058-628aafc7d610?auto=format&fit=crop&w=1600&q=80',
};

// Gradient overlays for each category
const CATEGORY_GRADIENTS = {
  'sports-discussion': 'from-red-500/30 to-dark-50/95',
  'fitness-wellness': 'from-green-500/30 to-dark-50/95',
  'club-recommendations': 'from-gold-500/30 to-dark-50/95',
  'business-networking': 'from-blue-500/30 to-dark-50/95',
  'travel-locations': 'from-purple-500/30 to-dark-50/95',
  'general-discussion': 'from-teal-500/30 to-dark-50/95',
  'the-rendezvous': 'from-pink-500/30 to-dark-50/95',
};

// Light mode gradient overlays with higher opacity
const LIGHT_CATEGORY_GRADIENTS = {
  'sports-discussion': 'from-red-500/40 to-light-100/70',
  'fitness-wellness': 'from-green-500/40 to-light-100/70',
  'club-recommendations': 'from-gold-500/40 to-light-100/70',
  'business-networking': 'from-blue-500/40 to-light-100/70',
  'travel-locations': 'from-purple-500/40 to-light-100/70',
  'general-discussion': 'from-teal-500/40 to-light-100/70',
  'the-rendezvous': 'from-pink-500/40 to-light-100/70',
};

/* ───────────────────────── icon helper ────────────────────────── */
const iconProps = { className: 'h-5 w-5 text-gold-400' };

const ICONS: Record<string, React.ComponentType<any>> = {
  MessageSquare,
  Users,
  Calendar,
  Shield,
  Dumbbell,
  Trophy,
  MapPin,
  Briefcase,
  Coffee,
  Leaf
};

const getIconComponent = (name: string) => {
  const Icon = ICONS[name] ?? MessageSquare;
  return <Icon {...iconProps} />;
};
/* ──────────────────────────────────────────────────────────────── */

const ForumCategory: React.FC = () => {
  /* ───────── hooks & state ───────── */
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();

  const [category, setCategory] = useState<CategoryType | null>(null);
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<ForumTopic | null>(null);
  const [replies, setReplies] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isTopicLoading, setIsTopicLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [authorClubsMap, setAuthorClubsMap] = useState<Record<string, ClubBadge[]>>({});

  /* ───────── load category + topics ───────── */
  useEffect(() => {
    setError(null);
    if (!slug) return;

    (async () => {
      setIsLoading(true);
      try {
        const cat = await getForumCategoryBySlug(slug);
        if (!cat) {
          setError('Category not found');
          return;
        }
        setCategory(cat);
        setTopics(await getCategoryTopics(cat.id));
      } catch (e) {
        console.error(e);
        setError('Failed to load category');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [slug]);

useEffect(() => {
  if (!topics.length) return;

  const missingAuthorIds = topics
    .map(t => t.author.id)
    .filter((id, idx, arr) => arr.indexOf(id) === idx)   // unique IDs
    .filter(id => !authorClubsMap[id]);                  // not yet fetched

  if (!missingAuthorIds.length) return;

  (async () => {
    const newMap = { ...authorClubsMap };

    await Promise.all(
      missingAuthorIds.map(async authorId => {
        try {
          const clubs = await getUserClubBadges(authorId);
          newMap[authorId] = clubs;
        } catch (err) {
          console.error(`Failed loading clubs for ${authorId}:`, err);
          newMap[authorId] = [];
        }
      })
    );

    setAuthorClubsMap(newMap);
  })();
}, [topics, authorClubsMap]);


  
  /* ───────── open topic view ───────── */
  const handleTopicClick = async (topic: ForumTopic) => {
    setIsTopicLoading(true);
    try {
      const t = await getTopic(topic.id);
      if (!t) return;
      setSelectedTopic(t);
      setReplies(await getTopicReplies(topic.id));
    } finally {
      setIsTopicLoading(false);
    }
  };

  const handleCloseTopicView = () => {
    setSelectedTopic(null);
    setReplies([]);
  };

 // Replace your existing handleSubmitReply entirely with this:
const handleSubmitReply = async (
  content: string,
  mentionedUsers: Member[],
  media?: File
) => {
  if (!selectedTopic || (!content.trim() && !media)) return;
  setIsSubmitting(true);
  try {
    const newReply = await createReply(
      content.trim(),
      selectedTopic.id,
      media,
      mentionedUsers
    );

    // optimistic UI update
   
setReplies(prev => [
  ...prev,
  {
    id:        newReply.id,
    content:   newReply.content,
    media:     newReply.media,
    author: {
      id:    newReply.author.id,
      name:  newReply.author.name,
      image: newReply.author.image,
    },
    createdAt: newReply.createdAt,
    likes:     newReply.likes ?? 0,
    isLiked:   newReply.isLiked ?? false,
  }
]);


  // bump topic metadata
setSelectedTopic(prev =>
  prev && {
    ...prev,
    repliesCount:    prev.repliesCount + 1,
    lastReplyAt:     newReply.createdAt,
    lastReplyAuthor: {
      id:    newReply.author.id,
      name:  newReply.author.name,
      image: newReply.author.image,
    }
  }
);

  } catch (error) {
    console.error('Error creating reply:', error);
  } finally {
    setIsSubmitting(false);
  }
};

  /* ───────── header navigation ───────── */
  const handleNewTopic = () =>
    navigate(`/dashboard/forums/new-topic${category ? `?category=${category.id}` : ''}`);

  /* ───────── render topic view ───────── */
  const renderTopicView = () => {
    if (!selectedTopic) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={handleCloseTopicView}
            className="flex items-center text-dark-800 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Topics
          </button>
        </div>

        {isTopicLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
          </div>
        ) : (
          <>
            <TopicHeader 
              topic={selectedTopic} 
              onEditClick={handleEditTopic} 
              onDeleteClick={handleDeleteTopic} 
            />
            <TopicContent topic={selectedTopic} />
            <ReplyList replies={replies} />
            <ReplyForm onSubmit={handleSubmitReply} isSubmitting={isSubmitting} />
          </>
        )}
      </div>
    );
  };

  /* ───────── edit topic ───────── */
  const handleEditTopic = () => {
    if (!selectedTopic) return;
    setEditTitle(selectedTopic.title);
    setEditContent(selectedTopic.content);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedTopic || !editTitle.trim() || !editContent.trim()) return;
    
    setIsSubmitting(true);
    try {
      const success = await updateTopic(selectedTopic.id, editTitle, editContent);
      
      if (success) {
        // Update the selected topic in the UI
        setSelectedTopic(prev => {
          if (!prev) return null;
          return {
            ...prev,
            title: editTitle,
            content: editContent,
            updatedAt: new Date().toISOString()
          };
        });
        
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error updating topic:', err);
      setError(err instanceof Error ? err.message : 'Failed to update topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ───────── delete topic ───────── */
  const handleDeleteTopic = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTopic) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteTopic(selectedTopic.id);
      
      if (success) {
        // Remove the topic from the list and close the topic view
        setTopics(prev => prev.filter(t => t.id !== selectedTopic.id));
        setSelectedTopic(null);
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      console.error('Error deleting topic:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete topic');
    } finally {
      setIsDeleting(false);
    }
  };

  /* ───────── error screen ───────── */
  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <button
            onClick={() => navigate('/dashboard/forums')}
            className="flex items-center text-dark-800 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Forums
          </button>

          <div className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-16 w-16 text-dark-800 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">{error}</h2>
            <p className="text-dark-800 mb-6">
              The category you're looking for doesn't exist or has been removed.
            </p>
            <Button
              onClick={() => navigate('/dashboard/forums')}
              className="bg-gold-400 hover:bg-gold-500 text-dark-50"
            >
              Back to Forums
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* ───────── main render ───────── */
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* back button */}
        <button
          onClick={() => navigate('/dashboard/forums')}
          className="flex items-center text-dark-800 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Forums
        </button>

        {/* category header */}
        <div
          className={cn(
            "relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4",
            "pb-4 border-b mb-6 rounded-xl p-4 overflow-hidden",
            isDarkMode ? "border-dark-300" : "border-light-300",
            isDarkMode ? "bg-dark-200" : "bg-light-100"
          )}
        >
          {/* Background image with gradient overlay */}
          {category && (
            <>
              <div 
                className={cn(
                  "absolute inset-0 z-0",
                  isDarkMode ? "opacity-60" : "opacity-75"
                )}
                style={{
                  backgroundImage: `url(${CATEGORY_BACKGROUNDS[category.slug] || ''})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className={cn(
                "absolute inset-0 z-0",
                isDarkMode 
                  ? `bg-gradient-to-r ${CATEGORY_GRADIENTS[category.slug] || 'from-dark-300/50 to-dark-50/95'}`
                  : `bg-gradient-to-r ${LIGHT_CATEGORY_GRADIENTS[category.slug] || 'from-light-200/60 to-light-100/80'}`
              )} />
              {/* NEW: subtle dark veil only in light mode */}
              {!isDarkMode && (
                <div className="absolute inset-0 z-[1] rounded-xl bg-black/25 pointer-events-none" />
              )}
 {!isDarkMode && (
   <div className="absolute inset-0 z-[1] rounded-xl bg-black/25 pointer-events-none" />
 )}

              
            </>
          )}
          
          {/* Content */}
        {/* ───────── header content ───────── */}
<div className="relative z-10">
  <div>
    {isLoading ? (
      <div className="flex items-center space-x-2">
        <Loader2 className="h-5 w-5 text-white/70 animate-spin" />
        <span className="text-white/70">Loading category...</span>
      </div>
    ) : (
      <>
        {/* title with gold gradient */}
        <h1
          className={cn(
            "text-2xl md:text-3xl lg:text-4xl flex items-center gap-2 font-semibold tracking-tight",
            "bg-gradient-to-r from-white via-amber-300 to-amber-500",
            "bg-clip-text text-transparent drop-shadow-lg"
          )}
        >
          {category?.icon && (
            <span
              className={cn(
                "inline-flex items-center justify-center w-8 h-8 rounded-full shadow-sm",
                isDarkMode ? "bg-dark-300/90" : "bg-light-300"
              )}
            >
              {getIconComponent(category.icon)}
            </span>
          )}
          {category?.name}
        </h1>

        {/* description — same colour in both themes */}
        <p className="text-white/90 mt-1 font-medium drop-shadow-sm">
          {category?.description}
        </p>
      </>
    )}
  </div>
</div>

          <div className="relative z-10">
            <Button
              onClick={handleNewTopic} 
              className="bg-gold-400 hover:bg-gold-500 text-dark-50 flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span>New Topic</span>
            </Button>
          </div>
        </div>

        {/* topic list or topic view */}
        {selectedTopic ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={handleCloseTopicView}
                className="flex items-center text-dark-800 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Topics
              </button>
            </div>

            {isTopicLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
              </div>
            ) : (
              <>
                <TopicHeader 
                  topic={selectedTopic} 
                  onEditClick={handleEditTopic} 
                  onDeleteClick={handleDeleteTopic} 
                />
                <TopicContent topic={selectedTopic} />
                <ReplyList replies={replies} setReplies={setReplies} />
                <ReplyForm onSubmit={handleSubmitReply} isSubmitting={isSubmitting} />
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className={cn(
                "text-xl font-semibold",
                isDarkMode ? "text-white" : "text-light-900"
              )}>Topics</h2>
              <div className={isDarkMode ? "text-white/70" : "text-light-600"}>
                {!isLoading && `${topics.length} ${topics.length === 1 ? 'topic' : 'topics'}`}
              </div>
            </div>

            {isLoading ? (
              <TopicListSkeleton count={5} />
            ) : topics.length ? (
              topics.map(topic => (
                <div
                  key={topic.id}
                  className={cn(
                    'p-4 sm:p-6 rounded-xl border cursor-pointer transition-all duration-200 mb-4',
                    'hover:border-gold-400 hover:shadow-md',
                    topic.isPinned && 'border-gold-400/50',
                    isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-light-100 border-light-300'
                  )}
                  onClick={() => handleTopicClick(topic)}
                >
                  <div className="flex items-start gap-4">
                    <img
                      src={topic.author.image}
                      alt={topic.author.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3
                          className={cn(
                            'text-lg font-medium',
                            topic.isPinned && 'text-gold-400',
                            isDarkMode ? 'text-white' : 'text-light-900 font-bold'
                          )}
                        >
                          {topic.title}
                        </h3>
                        {topic.isPinned && <Pin className="h-4 w-4 text-gold-400" />}
                        {topic.isLocked && <Lock className="h-4 w-4 text-red-400" />}
                      </div>
                      <div className={cn(
                        "text-sm mt-1",
                        isDarkMode ? "text-white/70" : "text-light-600"
                      )}>
                        Started by {topic.author.name}
                        {topic.author.city && ` from ${topic.author.city}`}{' '}
                        • {formatRelativeTime(topic.createdAt)}
{authorClubsMap[topic.author.id]?.length > 0 && (
  <div className="mt-1">
    <ClubBadges
      clubs={authorClubsMap[topic.author.id]}
      size="sm"
      limit={3}
    />
  </div>
)}

                        
                      </div>

                      <div className={cn(
                        "flex items-center gap-4 mt-3 text-sm",
                        isDarkMode ? "text-white/70" : "text-light-600"
                      )}>
                        <div className="flex items-center">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          {topic.repliesCount}{' '}
                          {topic.repliesCount === 1 ? 'reply' : 'replies'}
                        </div>
                        <div className="flex items-center">
                          <Eye className="h-4 w-4 mr-1" />
                          {topic.viewsCount} {topic.viewsCount === 1 ? 'view' : 'views'}
                        </div>
                      </div>
                    </div>

                    {topic.lastReplyAt && topic.lastReplyAuthor && (
                      <div className={cn(
                        "text-right text-sm hidden sm:block", 
                        isDarkMode ? "text-white/70" : "text-light-700 font-medium"
                      )}>
                        <div className={cn(
                          "text-sm mb-1",
                          isDarkMode ? "text-dark-800" : "text-light-700 font-medium"
                        )}>Latest reply</div>
                        <div className="flex items-center justify-end space-x-2">
                          <div className="text-right">
                            <div className={cn(
                              "text-sm",
                              isDarkMode ? "text-white" : "text-light-900 font-semibold"
                            )}>{topic.lastReplyAuthor.name}</div>
                            <div className={cn(
                              "text-xs",
                              isDarkMode ? "text-dark-800" : "text-light-700"
                            )}>{formatRelativeTime(topic.lastReplyAt)}</div>
                          </div>
                          <img
                            src={topic.lastReplyAuthor.image}
                            alt={topic.lastReplyAuthor.name}
                            className={cn(
                              "w-8 h-8 rounded-full object-cover border",
                              isDarkMode ? "border-dark-400" : "border-light-400"
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div
                className={cn(
                  'p-8 rounded-xl border text-center',
                  isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-light-200 border-light-300'
                )}
              >
                <MessageSquare className={cn(
                  "h-12 w-12 mx-auto mb-4",
                  isDarkMode ? "text-dark-800" : "text-light-600"
                )} />
                <h3 className={cn(
                  "text-lg font-medium mb-2",
                  isDarkMode ? "text-white" : "text-light-900"
                )}>No topics yet</h3>
                <p className={cn(
                  "mb-4",
                  isDarkMode ? "text-dark-800" : "text-light-700"
                )}>
                  Be the first to start a discussion in this category.
                </p>
                <Button
                  onClick={handleNewTopic}
                  className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                >
                  Start a New Topic
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Edit Topic Dialog */}
      {isEditing && selectedTopic && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={cn(
            "w-full max-w-2xl rounded-xl p-6 space-y-4",
            isDarkMode ? "bg-dark-200 border border-dark-300" : "bg-light-100 border border-light-300"
          )}>
            <h2 className="text-xl font-semibold text-white">Edit Topic</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-dark-800 mb-2">Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={cn(
                    "w-full p-3 rounded-lg border",
                    isDarkMode 
                      ? "bg-dark-300 border-dark-400 text-white" 
                      : "bg-light-200 border-light-300 text-light-900"
                  )}
                />
              </div>
              
              <div>
                <label className="block text-dark-800 mb-2">Content</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={10}
                  className={cn(
                    "w-full p-3 rounded-lg border",
                    isDarkMode 
                      ? "bg-dark-300 border-dark-400 text-white" 
                      : "bg-light-200 border-light-300 text-light-900"
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-2">
              <Button
                onClick={() => setIsEditing(false)}
                variant="outline"
                className={isDarkMode ? "border-dark-400" : "border-light-300"}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={isSubmitting || !editTitle.trim() || !editContent.trim()}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={cn(
            "w-full max-w-md rounded-xl p-6 space-y-4 text-center",
            isDarkMode ? "bg-dark-200 border border-dark-300" : "bg-light-100 border border-light-300"
          )}>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mb-2">
              <Trash className="h-6 w-6 text-red-500" />
            </div>
            
            <h2 className="text-xl font-semibold text-white">Delete Topic</h2>
            <p className="text-dark-800">
              Are you sure you want to delete this topic? This action cannot be undone.
            </p>
            
            <div className="flex justify-center space-x-3 pt-2">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className={isDarkMode ? "border-dark-400" : "border-light-300"}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {isDeleting ? 'Deleting...' : 'Delete Topic'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ForumCategory;